// B2.4 Client Delivery — pure editor/template model (no React, no network).
// Kept framework-free so template/block/context logic is deterministically testable.

export const TEMPLATE_TYPES = [
  "lesson_summary", "work_plan", "exercise_sheet",
  "pathway_summary", "general_indications", "free_document",
];

export const TEMPLATE_LABELS = {
  lesson_summary: "Resoconto lezione",
  work_plan: "Piano di lavoro",
  exercise_sheet: "Scheda esercizi",
  pathway_summary: "Riepilogo percorso",
  general_indications: "Indicazioni generali",
  free_document: "Documento libero",
};

export const CONTEXT_LABELS = {
  general: "Generale",
  session: "Collegata a lezione/prestazione",
};

export const STATE_LABELS = {
  draft: "Bozza",
  finalized: "Finalizzata",
};

export const templateLabel = (tt) => TEMPLATE_LABELS[tt] || "Documento";

// Editable default title derived from the template (no sensitive/internal content).
export const defaultTitleFor = (tt) => ({
  lesson_summary: "Resoconto lezione",
  work_plan: "Piano di lavoro",
  exercise_sheet: "Scheda esercizi",
  pathway_summary: "Riepilogo percorso",
  general_indications: "Indicazioni generali",
  free_document: "Documento",
}[tt] || "Documento");

let _seq = 0;
export const localBlockId = () => `local-${Date.now()}-${_seq++}`;

export const makeTextBlock = (title = "", content = "") => ({ id: localBlockId(), type: "TEXT", title, content });
export const makeExerciseBlock = (name = "") => ({
  id: localBlockId(), type: "EXERCISE",
  name, goal: "", instructions: "", when_to_use: "", frequency: "", duration: "", cautions: "",
});

// Starting structures (editable, not rigid schemas). No dog/client-specific content prefilled.
export const defaultBlocksFor = (tt) => {
  switch (tt) {
    case "lesson_summary":
      return [
        makeTextBlock("Cosa abbiamo fatto"),
        makeTextBlock("Come è andata"),
        makeExerciseBlock("Esercizi"),
        makeTextBlock("Indicazioni fino al prossimo incontro"),
      ];
    case "work_plan":
      return [
        makeTextBlock("Situazione attuale"),
        makeTextBlock("Obiettivi"),
        makeTextBlock("Strategia di lavoro"),
        makeExerciseBlock("Esercizi"),
        makeTextBlock("Gestione quotidiana"),
        makeTextBlock("Raccomandazioni"),
      ];
    case "exercise_sheet":
      return [makeExerciseBlock("")];
    case "pathway_summary":
      return [
        makeTextBlock("Punto di partenza"),
        makeTextBlock("Progressi osservati"),
        makeTextBlock("Aspetti ancora da lavorare"),
        makeTextBlock("Indicazioni successive"),
      ];
    case "general_indications":
      return [makeTextBlock("Indicazioni generali")];
    case "free_document":
    default:
      return [makeTextBlock("")];
  }
};

// --- Block operations (immutable, order-preserving) ---
export const moveBlock = (blocks, index, dir) => {
  const to = index + dir;
  if (to < 0 || to >= blocks.length) return blocks;
  const next = blocks.slice();
  [next[index], next[to]] = [next[to], next[index]];
  return next;
};
export const removeBlock = (blocks, id) => blocks.filter((b) => b.id !== id);
export const updateBlock = (blocks, id, patch) => blocks.map((b) => (b.id === id ? { ...b, ...patch } : b));
export const addBlock = (blocks, block) => [...blocks, block];

const TEXT_FIELDS = ["title", "content"];
const EXERCISE_FIELDS = ["name", "goal", "instructions", "when_to_use", "frequency", "duration", "cautions"];

export const blockHasContent = (b) => {
  if (!b) return false;
  const fields = b.type === "EXERCISE" ? EXERCISE_FIELDS : TEXT_FIELDS;
  return fields.some((k) => (b[k] || "").trim() !== "");
};

// Normalize blocks for the save payload: send only contract fields, preserve order & ids.
export const toPayloadBlocks = (blocks) =>
  (blocks || []).map((b) => {
    if (b.type === "EXERCISE") {
      const out = { id: b.id, type: "EXERCISE" };
      EXERCISE_FIELDS.forEach((k) => { out[k] = b[k] || ""; });
      return out;
    }
    return { id: b.id, type: "TEXT", title: b.title || "", content: b.content || "" };
  });

// Build the creation body honoring the backend context invariant:
// general -> no session_id ; session -> session_id required.
export const buildCreateBody = ({ templateType, context, sessionId, title }) => {
  if (!TEMPLATE_TYPES.includes(templateType)) throw new Error("template_type non valido");
  const body = {
    title: title != null ? title : defaultTitleFor(templateType),
    template_type: templateType,
    document_type: context,
    blocks: toPayloadBlocks(defaultBlocksFor(templateType)),
  };
  if (context === "session") {
    if (!sessionId) throw new Error("session richiede session_id");
    body.session_id = sessionId;
  } else {
    body.document_type = "general";
    body.session_id = null;
  }
  return body;
};

// --- Save state machine (pure) ---
// status: 'ok' | 'conflict' | 'error'; seqChanged = a newer edit happened during the request.
export const saveResult = ({ status, seqChanged }) => {
  if (status === "ok") {
    if (seqChanged) return { saveState: "unsaved", clearDirty: false, resave: true };
    return { saveState: "saved", clearDirty: true, resave: false };
  }
  if (status === "conflict") return { saveState: "conflict", clearDirty: false, resave: false };
  return { saveState: "error", clearDirty: false, resave: false };
};

// The shared offline latch can go stale after reconnect; probe to self-heal before a write.
export const needsConnectivityProbe = (apiOnline, navigatorOnline) => !apiOnline && navigatorOnline !== false;

// Internal-navigation guard: leaving with unconfirmed local changes must confirm.
export const navGuardDecision = ({ dirty, saving, hasPendingTimer }) =>
  (dirty || saving || hasPendingTimer) ? "confirm" : "proceed";

export const canDeleteDraft = (doc) => !!doc && doc.state === "draft";

// Linked prestation display (canonical session metadata; never mutated).
export const formatLinkedSession = (s) => {
  if (!s) return null;
  return {
    title: s.service_type || "Prestazione",
    date: s.date || "",
    time: s.start_time || "",
    status: s.status || s.session_status || "",
  };
};

// --- T3: explicit K9 import (presentation only; backend remains authoritative) ---
export const DIARY_IMPORT_FIELDS = [
  ["work_done", "Lavoro svolto"],
  ["progress_outcome", "Esito / progressi osservati"],
  ["next_step", "Prossimo passo"],
];
export const HOMEWORK_IMPORT_FIELDS = [
  ["title", "Titolo"],
  ["instructions", "Istruzioni"],
  ["frequency", "Frequenza"],
  ["duration", "Durata"],
  ["owner_note", "Nota per il proprietario"],
];
export const canImportInto = (doc) => !!doc && doc.state === "draft";

// --- T3 F-01/F-03: import safety coordinators (framework-free, unit-testable) ---
// Close is denied while an import is authoritative/in-flight, so the editor behind the
// modal stays unreachable and no local edit can race the import response.
export const importCloseAllowed = (busy) => !busy;

// Serializes one import at a time, releases busy on EVERY path, closes only on success.
export async function runImportConfirm({ getBusy, setBusy, importFn, onClose }) {
  if (getBusy && getBusy()) return "ignored"; // no concurrent second import
  setBusy(true);
  try {
    const ok = await importFn();
    if (ok) { onClose(); return "closed"; }
    return "stay"; // failure: dialog stays open, retryable
  } finally {
    setBusy(false); // busy released on success, failure and throw
  }
}

// F-03: preliminary flush must succeed before the import POST; a flush/dirty failure must
// NOT send the POST, must preserve local content, and must never claim success.
export async function performImport({ flush, isDirty, post, onSuccess, onConflict, onError }) {
  try {
    await flush();
  } catch (e) {
    if (onError) onError("flush", e);
    return false;
  }
  if (isDirty()) {
    if (onError) onError("dirty");
    return false;
  }
  try {
    const data = await post();
    if (onSuccess) onSuccess(data);
    return true;
  } catch (e) {
    const status = e && e.response && e.response.status;
    if (status === 409) { if (onConflict) onConflict(); }
    else if (onError) onError("post", e);
    return false;
  }
}

// --- T4.1: client-facing presentation (pure; the ONLY truth is the canonical revision) ---
// Polished owner-facing EXERCISE labels. T4.2 (copy/share) will reuse this same projection.
export const EXERCISE_PREVIEW_FIELDS = [
  ["name", "Esercizio"],
  ["goal", "Obiettivo"],
  ["instructions", "Come farlo"],
  ["when_to_use", "Quando usarlo"],
  ["frequency", "Frequenza"],
  ["duration", "Durata"],
  ["cautions", "Note e attenzioni"],
];

// Project a canonical revision (or live editor {title, blocks}) into client-facing content.
// Drops provenance, ids, record_version, source metadata, empty fields and empty blocks.
export function toClientFacing(doc) {
  const blocks = (doc && doc.blocks ? doc.blocks : [])
    .map((b) => {
      if (b.type === "EXERCISE") {
        const fields = EXERCISE_PREVIEW_FIELDS
          .map(([k, label]) => ({ key: k, label, value: ((b[k] || "") + "").trim() }))
          .filter((f) => f.value);
        return { type: "EXERCISE", fields };
      }
      return { type: "TEXT", title: ((b.title || "") + "").trim(), content: ((b.content || "") + "").trim() };
    })
    .filter((b) => (b.type === "TEXT" ? (b.title || b.content) : b.fields.length > 0));
  return { title: ((doc && doc.title) || "").trim(), revision_number: doc && doc.revision_number, blocks };
}

export const clientFacingIsEmpty = (cf) => !cf || (!cf.title && (!cf.blocks || cf.blocks.length === 0));

// --- T4.1 UX01: one-time title select on fresh-from-template drafts only ---
export function makeTitlePreselect(fromTemplate) {
  let armed = !!fromTemplate;
  return {
    shouldSelect: () => armed,
    consume: () => { armed = false; },
  };
}

// --- T4.1 UX03: group revisions into families by stable delivery_id (never by title) ---
export function groupDeliveryFamilies(items) {
  const fam = new Map();
  for (const it of items || []) {
    const key = it.delivery_id || it.id;
    if (!fam.has(key)) fam.set(key, []);
    fam.get(key).push(it);
  }
  const families = [];
  for (const [delivery_id, revs] of fam) {
    const revisions = [...revs].sort((a, b) => (b.revision_number || 0) - (a.revision_number || 0));
    families.push({ delivery_id, latest: revisions[0], revisions, count: revisions.length });
  }
  families.sort((a, b) => ((b.latest.updated_at || "").localeCompare(a.latest.updated_at || "")));
  return families;
}
// --- T4.2: canonical client-facing serialization (pure) ---
// The ONLY input is a toClientFacing() projection; raw delivery fields are never read here,
// so internal_notes / provenance / ids / record_version cannot reach copied or shared text.
export function clientFacingToText(cf) {
  if (!cf) return "";
  const sections = [];
  if (cf.title) sections.push(cf.title);
  (cf.blocks || []).forEach((b) => {
    if (b.type === "TEXT") {
      const parts = [];
      if (b.title) parts.push(b.title);
      if (b.content) parts.push(b.content);
      if (parts.length) sections.push(parts.join("\n"));
    } else if (b.type === "EXERCISE") {
      const fields = (b.fields || []).filter((f) => f && f.value);
      if (!fields.length) return;
      const parts = [];
      fields.forEach((f) => {
        if (f.key === "name") parts.push(`Esercizio: ${f.value}`);
        else { parts.push(f.label); parts.push(f.value); }
      });
      sections.push(parts.join("\n"));
    }
  });
  return sections
    .map((s) => s.split("\n").map((l) => l.replace(/\s+$/, "")).join("\n"))
    .join("\n\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

// Single payload for BOTH Copy and native Share (one serializer, no second content truth).
export function buildClientSharePayload(cf) {
  return { title: (cf && cf.title) || "Consegna", text: clientFacingToText(cf) };
}

// Preview of the Homework -> EXERCISE mapping (goal/when_to_use never invented).
export const homeworkToExercisePreview = (f) => ({
  name: (f && f.title) || "", goal: "", instructions: (f && f.instructions) || "",
  when_to_use: "", frequency: (f && f.frequency) || "", duration: (f && f.duration) || "", cautions: (f && f.owner_note) || "",
});
